import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Button, Chip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Divider,
  InputAdornment, CircularProgress, Avatar, Tooltip, Accordion,
  AccordionSummary, AccordionDetails, TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon, Edit as EditIcon, Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon, Close as CloseIcon,
  Save as SaveIcon, Gavel as GavelIcon, Assignment as AssignmentIcon,
  ArrowBack as ArrowBackIcon, People as PeopleIcon, Download as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  adminListarServidores, adminAtualizarServidor, adminBuscarDocumentosServidor,
  getAdminToken, adminBaixarServidoresXlsx,
} from '../../services/adminApi';

// ── helpers ───────────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const getToken = () => getAdminToken() || '';

const formatDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

// ── EditDialog ────────────────────────────────────────────────────────────────
const EditDialog: React.FC<{
  open: boolean; servidor: any; onClose: () => void; onSaved: () => void;
}> = ({ open, servidor, onClose, onSaved }) => {
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (servidor) {
      // suporta tanto objeto direto quanto payload { servidor, ... }
      setForm({ ...(servidor.servidor ?? servidor) });
    }
  }, [servidor]);

  const field = (key: string, label: string, type = 'text', xs = 6) => (
    <Grid item xs={12} sm={xs}>
      <TextField fullWidth label={label} type={type} value={form[key] ?? ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        size="small" variant="outlined" />
    </Grid>
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const srv = servidor.servidor ?? servidor;
      await adminAtualizarServidor(srv.id, form);
      toast.success('Servidor atualizado!');
      onSaved(); onClose();
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>Editar Servidor</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {field('nome_completo', 'Nome Completo', 'text', 12)}
          {field('cpf', 'CPF')} {field('email', 'E-mail')}
          {field('telefone', 'Telefone')} {field('estado_civil', 'Estado Civil')}
          {field('identidade', 'Identidade')}
          <Grid item xs={12}><Divider><Typography variant="caption">Endereço</Typography></Divider></Grid>
          {field('endereco', 'Endereço', 'text', 8)} {field('endereco_numero', 'Número', 'text', 4)}
          {field('bairro', 'Bairro')} {field('cidade', 'Cidade')}
          {field('estado', 'UF', 'text', 3)} {field('cep', 'CEP', 'text', 3)}
          <Grid item xs={12}><Divider><Typography variant="caption">Dados Bancários</Typography></Divider></Grid>
          {field('banco', 'Banco')} {field('agencia', 'Agência')}
          {field('conta', 'Conta')} {field('tipo_conta', 'Tipo Conta')} {field('pix', 'PIX')}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── DetailDialog ──────────────────────────────────────────────────────────────
const DetailDialog: React.FC<{
  open: boolean;
  // payload shape: { servidor, matriculas, totais } OU objeto direto (fallback)
  servidor: any;
  loadingDetail: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDocumentos: (tipo: 'CONTRATO' | 'SOLICITACAO') => void;
}> = ({ open, servidor: payload, loadingDetail, onClose, onEdit, onDocumentos }) => {
  if (!payload) return null;

  const srv        = payload.servidor ?? payload;
  const matriculas: any[] = payload.matriculas ?? [];

  const info = (label: string, value: any) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary"
        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.2 }}>{value || '—'}</Typography>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>

      {/* Header */}
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        bgcolor: 'primary.main', color: 'white', py: 2.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{
            bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44,
            border: '2px solid rgba(255,255,255,0.3)',
          }}>
            {srv.nome_completo?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={700} color="white">{srv.nome_completo}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              CPF: {srv.cpf}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loadingDetail ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">Carregando dados completos...</Typography>
          </Box>
        ) : (
          <>
            {/* ── 1. Dados Pessoais ── */}
            <Accordion defaultExpanded disableGutters elevation={0}
              sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, minHeight: 52 }}>
                <Typography fontWeight={700} variant="body2">Dados Pessoais</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Grid container spacing={2}>
                  {info('Nome Completo', srv.nome_completo)}
                  {info('CPF', srv.cpf)}

                  {/* Matrículas como chips */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem', display: 'block' }}>
                      Matrículas
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {matriculas.length > 0
                        ? matriculas.map((m: any) => (
                            <Chip key={m.id} size="small" label={m.matricula}
                              sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 700, fontSize: '0.72rem' }} />
                          ))
                        : <Typography variant="body2" fontWeight={500}>—</Typography>
                      }
                    </Box>
                  </Grid>

                  {info('Telefone', srv.telefone)}
                  {info('E-mail', srv.email)}
                  {info('Estado Civil', srv.estado_civil)}
                  {info('Identidade', srv.identidade)}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── 2. Endereço ── */}
            <Accordion disableGutters elevation={0}
              sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, minHeight: 52 }}>
                <Typography fontWeight={700} variant="body2">Endereço</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Grid container spacing={2}>
                  {info('Logradouro', srv.endereco)}
                  {info('Número', srv.endereco_numero)}
                  {info('Bairro', srv.bairro)}
                  {info('Cidade', srv.cidade)}
                  {info('Estado', srv.estado)}
                  {info('CEP', srv.cep)}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── 3. Dados Bancários ── */}
            <Accordion disableGutters elevation={0}
              sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, minHeight: 52 }}>
                <Typography fontWeight={700} variant="body2">Dados Bancários</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 3 }}>
                <Grid container spacing={2}>
                  {info('Banco', srv.banco)}
                  {info('Agência', srv.agencia)}
                  {info('Conta', srv.conta)}
                  {info('Tipo de Conta', srv.tipo_conta)}
                  {info('PIX', srv.pix)}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* ── 4. Documentos ── */}
            <Box sx={{ px: 3, py: 3, bgcolor: '#FAFAFA' }}>
              <Typography variant="overline" color="text.disabled"
                sx={{ fontWeight: 700, letterSpacing: 1.5, fontSize: '0.65rem', display: 'block', mb: 2 }}>
                Documentos
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button fullWidth variant="outlined" size="large" startIcon={<GavelIcon />}
                    onClick={() => onDocumentos('CONTRATO')}
                    sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                    Contratos
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button fullWidth variant="outlined" size="large" color="success"
                    startIcon={<AssignmentIcon />} onClick={() => onDocumentos('SOLICITACAO')}
                    sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
                    Solicitações
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" startIcon={<EditIcon />} onClick={onEdit}>Editar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── DocumentosDialog ──────────────────────────────────────────────────────────
const DocumentosDialog: React.FC<{
  open: boolean; servidor: any; tipo: 'CONTRATO' | 'SOLICITACAO' | null; onClose: () => void;
}> = ({ open, servidor, tipo, onClose }) => {
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      // suporta tanto objeto direto quanto payload { servidor, ... }
      const srv = servidor.servidor ?? servidor;
      const all = await adminBuscarDocumentosServidor(srv.id);
      setDocs(all);
    } catch { toast.error('Erro ao carregar documentos'); }
    finally { setLoading(false); }
  }, [servidor]);

  useEffect(() => {
    if (open && servidor) loadDocs();
    if (!open) setDocs([]);
  }, [open, servidor, loadDocs]);

  const srv      = servidor?.servidor ?? servidor;
  const filtered = tipo ? docs.filter((d) => d.tipo === tipo) : docs;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {tipo === 'CONTRATO' ? 'Contratos' : 'Solicitações'}
          </Typography>
          <Typography variant="caption" color="text.secondary">{srv?.nome_completo}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
            Nenhum documento encontrado
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {['Arquivo', 'Status', 'Assinado em', 'Tamanho'].map(h => (
                    <TableCell key={h}
                      sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase',
                        color: 'text.secondary', letterSpacing: 0.4 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((doc) => (
                  <TableRow key={doc.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{doc.nome_arquivo}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={doc.status} size="small"
                        color={doc.status === 'ASSINADO' ? 'success'
                          : doc.status === 'PENDENTE' ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell>{formatDate(doc.data_assinatura)}</TableCell>
                    <TableCell>
                      {doc.tamanho ? `${(doc.tamanho / 1024).toFixed(1)} KB` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fechar</Button></DialogActions>
    </Dialog>
  );
};

const ServidoresSection: React.FC<{
  title: string;
  description: string;
  rows: any[];
  loading: boolean;
  total: number;
  page: number;
  rowsPerPage: number;
  badgeColor: 'primary' | 'secondary';
  emptyLabel: string;
  onOpenDetail: (s: any) => void;
  onOpenEdit: (s: any) => void;
  onOpenDocs: (s: any, tipo: 'CONTRATO' | 'SOLICITACAO') => void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}> = ({
  title, description, rows, loading, total, page, rowsPerPage, badgeColor, emptyLabel,
  onOpenDetail, onOpenEdit, onOpenDocs, onPageChange, onRowsPerPageChange,
}) => (
  <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
      <Box>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
      </Box>
      <Chip label={`${rows.length} registros`} color={badgeColor} variant="outlined" sx={{ fontWeight: 700 }} />
    </Box>

    {loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
    ) : (
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Servidor</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>CPF</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>E-mail</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Matrículas</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Documentos</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
            {rows.map((s) => (
              <TableRow key={s.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: s.oficio ? 'secondary.light' : 'primary.light', fontSize: 14 }}>
                      {s.nome_completo?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{s.nome_completo}</Typography>
                      <Chip
                        size="small"
                        label={s.oficio ? 'Ofício da Seduc' : 'Servidor regular'}
                        variant="outlined"
                        color={s.oficio ? 'secondary' : 'primary'}
                        sx={{ mt: 0.5, fontSize: '0.68rem', height: 22 }}
                      />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{s.cpf}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{s.email || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={s.total_matriculas ?? 0} size="small" variant="outlined" color="primary" />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title="Contratos">
                      <IconButton size="small" color="secondary" onClick={() => onOpenDocs(s, 'CONTRATO')}>
                        <GavelIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Solicitações">
                      <IconButton size="small" color="success" onClick={() => onOpenDocs(s, 'SOLICITACAO')}>
                        <AssignmentIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Visualizar">
                      <IconButton size="small" onClick={() => onOpenDetail(s)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" color="primary" onClick={() => onOpenEdit(s)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {emptyLabel}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    )}

    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_, nextPage) => onPageChange(nextPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={[10, 20, 50, 100]}
      labelRowsPerPage="Por página:"
      labelDisplayedRows={({ from, to, count: totalCount }) => `${from}–${to} de ${totalCount}`}
      sx={{ borderTop: '1px solid', borderColor: 'divider' }}
    />
  </Paper>
);

// ── Main ──────────────────────────────────────────────────────────────────────
const AdminServidoresPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const oficioOnly = location.pathname.includes('/oficio');

  const [servidoresRegulares, setServidoresRegulares] = useState<any[]>([]);
  const [servidoresOficio, setServidoresOficio]       = useState<any[]>([]);
  const [totalRegulares, setTotalRegulares]           = useState(0);
  const [totalOficio, setTotalOficio]                 = useState(0);
  const [loading, setLoading]                         = useState(true);
  const [page, setPage]                               = useState(0);
  const [rowsPerPage, setRowsPerPage]                 = useState(20);
  const [search, setSearch]                           = useState('');
  const [searchInput, setSearchInput]                 = useState('');
  const [selected, setSelected]                       = useState<any>(null);  // payload { servidor, matriculas, totais }
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailOpen, setDetailOpen]       = useState(false);
  const [editOpen, setEditOpen]           = useState(false);
  const [docsOpen, setDocsOpen]           = useState(false);
  const [docsTipo, setDocsTipo]           = useState<'CONTRATO' | 'SOLICITACAO' | null>(null);
  const [downloading, setDownloading]     = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListarServidores({
        page: page + 1,
        limit: rowsPerPage,
        search,
        oficio: oficioOnly,
      });

      const data = Array.isArray(res) ? res : (res.data || []);
      const total = Array.isArray(res) ? res.length : (res.total || 0);

      if (oficioOnly) {
        setServidoresOficio(data);
        setTotalOficio(total);
      } else {
        setServidoresRegulares(data);
        setTotalRegulares(total);
      }
    } catch { toast.error('Erro ao carregar servidores'); }
    finally { setLoading(false); }
  }, [oficioOnly, page, rowsPerPage, search]);

  useEffect(() => { load(); }, [load]);

  // ── Busca dados completos (endereço, banco, matrículas) ao abrir detalhe ──
  const openDetail = async (s: any) => {
    setSelected({ servidor: s, matriculas: [], totais: {} }); // exibe imediatamente
    setDetailOpen(true);
    setLoadingDetail(true);
    try {
      let res = await fetch(`${API_URL}/servidores/${s.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok && s.cpf) {
        res = await fetch(`${API_URL}/servidores/cpf/${s.cpf}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
      }
      if (res.ok) {
        const data = await res.json();
        setSelected(data.servidor ? data : { servidor: data, matriculas: [], totais: {} });
      }
    } catch (err) {
      console.error('[openDetail]', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const openEdit = (s: any) => { setSelected(s); setEditOpen(true); };

  const openDocs = (s: any, tipo: 'CONTRATO' | 'SOLICITACAO') => {
    setSelected(s); setDocsTipo(tipo); setDocsOpen(true);
  };

  const downloadServidores = async () => {
    setDownloading(true);
    try {
      const { blob, filename } = await adminBaixarServidoresXlsx();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Download iniciado');
    } catch {
      toast.error('Erro ao baixar servidores');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/admin')} size="small"><ArrowBackIcon /></IconButton>
        <Box>
          <Typography variant="h5" fontWeight={700}>Servidores</Typography>
          <Typography variant="body2" color="text.secondary">Gerenciamento de servidores cadastrados</Typography>
        </Box>
        <Chip icon={<PeopleIcon />} label={loading && (oficioOnly ? totalOficio : totalRegulares) === 0 ? '...' : `${oficioOnly ? totalOficio : totalRegulares} cadastrados`}
          color="primary" variant="outlined" sx={{ fontWeight: 700, fontSize: 14, height: 36, ml: 1 }} />
      </Box>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField fullWidth placeholder="Pesquisar por nome, CPF ou e-mail..."
              value={searchInput} onChange={(e) => setSearchInput(e.target.value)} size="small"
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>,
                endAdornment: loading && searchInput
                  ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                  : null,
              }} />
            <Button
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={downloadServidores}
              disabled={downloading}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Baixar
            </Button>
          </Box>
        </Box>
      </Paper>

      <ServidoresSection
        title={oficioOnly ? 'Servidores extraídos do Ofício da Seduc' : 'Servidores'}
        description={oficioOnly ? 'Registros importados da planilha do ofício' : 'Cadastros regulares do sistema'}
        rows={oficioOnly ? servidoresOficio : servidoresRegulares}
        loading={loading}
        total={oficioOnly ? totalOficio : totalRegulares}
        page={page}
        rowsPerPage={rowsPerPage}
        badgeColor={oficioOnly ? 'secondary' : 'primary'}
        emptyLabel={oficioOnly ? 'Nenhum servidor do ofício encontrado' : 'Nenhum servidor regular encontrado'}
        onOpenDetail={openDetail}
        onOpenEdit={openEdit}
        onOpenDocs={openDocs}
        onPageChange={setPage}
        onRowsPerPageChange={(nextRowsPerPage) => { setRowsPerPage(nextRowsPerPage); setPage(0); }}
      />

      <DetailDialog
        open={detailOpen}
        servidor={selected}
        loadingDetail={loadingDetail}
        onClose={() => setDetailOpen(false)}
        onEdit={() => { setDetailOpen(false); setEditOpen(true); }}
        onDocumentos={(tipo) => { setDetailOpen(false); openDocs(selected, tipo); }}
      />
      <EditDialog
        open={editOpen}
        servidor={selected}
        onClose={() => setEditOpen(false)}
        onSaved={load}
      />
      <DocumentosDialog
        open={docsOpen}
        servidor={selected}
        tipo={docsTipo}
        onClose={() => setDocsOpen(false)}
      />
    </Box>
  );
};

export default AdminServidoresPage;